import { Zap, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const TIER_CLS = {
  preventive: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  standard:   'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  formal:     'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  urgent:     'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  escalation: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const TIER_LABEL = {
  preventive: 'Preventivo',
  standard:   'Estándar',
  formal:     'Formal',
  urgent:     'Urgente',
  escalation: 'Escalación',
};

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0,
  }).format(Number(n ?? 0));
}

/**
 * CollectionCyclePreview — rich preview card for BULK_REMINDERS actions
 * triggered from the chat (recordatorio_facturas_vencidas tool).
 *
 * Shows tier breakdown, total amount, and "sin email" warning before the
 * user approves sending the cycle.
 */
export default function CollectionCyclePreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <Zap className="w-3.5 h-3.5" />
        <span>Ciclo de cobro aprobado — enviando recordatorios…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Ciclo de cobro cancelado</span>
      </div>
    );
  }

  const tierBreakdown = preview?.tier_breakdown ?? {};
  const tiers = Object.entries(tierBreakdown).filter(([, n]) => n > 0);

  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20 p-4 space-y-3 my-2 max-w-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Badge variant="outline" className="text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 text-[10px] mb-1">
            Requiere aprobación
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight">
            Ciclo de cobro · {preview?.facturas ?? 0} factura{(preview?.facturas ?? 0) !== 1 ? 's' : ''}
          </p>
        </div>
        <span className="text-lg font-bold text-indigo-700 dark:text-indigo-300 shrink-0 tabular-nums">
          {formatEur(preview?.total_vencido)}
        </span>
      </div>

      {tiers.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tiers.map(([tier, n]) => (
            <span key={tier}
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${TIER_CLS[tier] ?? ''}`}>
              {TIER_LABEL[tier] ?? tier} · {n}
            </span>
          ))}
        </div>
      )}

      {preview?.sin_email > 0 && (
        <p className="text-[11px] text-warning">
          ⚠ {preview.sin_email} sin email — se omitirán
        </p>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject}
          className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        <Button size="sm" onClick={onApprove}
          className="flex-1 h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
          <Zap className="w-3 h-3 mr-1" />Ejecutar ciclo
        </Button>
      </div>
    </div>
  );
}
